import { BulbOutlined, LogoutOutlined, SearchOutlined, UserOutlined } from '@ant-design/icons';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import styled from '@emotion/styled';

import useOnLogout from '../../hooks/auth/useOnLogout';
import { navGroups } from '../../layout/Dashboard/sideNav/navConfig';
import { PAGE_GUIDES } from '../../models/guides/pageGuides';
import { resetAllGuides } from '../Help/PageGuidePanel';
import { CUSTOMERS, GIFTS, SCHEDULES, TRANSACTIONS } from '../../routes/routeConstants';
import { formatPhone, looksLikePhone, toChatId } from '../../utils/utils';

const OPEN_EVENT = 'hethera:open-command-menu';

// Anything (sidebar, mobile top bar) can open the menu without sharing state.
export const openCommandMenu = () => window.dispatchEvent(new Event(OPEN_EVENT));

export const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
export const commandKeyLabel = isMac ? '⌘K' : 'Ctrl K';

interface Command {
  id: string;
  group: string;
  label: React.ReactNode;
  hint?: string;
  shortcut?: string;
  icon: React.ReactNode;
  run: () => void;
}

// "g" then a letter jumps to a page (Linear/GitHub style).
const SHORTCUTS: Record<string, string> = Object.fromEntries(
  navGroups.flatMap((g) => g.items).filter((i) => i.shortcut).map((i) => [i.shortcut as string, i.path])
);

// The `search` param is picked up by the list page's search box.
const SEARCHABLE = [
  { path: TRANSACTIONS, label: 'transactions', hint: 'Reference, customer or provider ref' },
  { path: SCHEDULES, label: 'scheduled payments', hint: 'Schedule code or phone number' },
  { path: GIFTS, label: 'Dash gifts', hint: 'Gift code or phone number' },
];

// ⌘K / Ctrl K (or "/") opens a palette to jump to any page or search a list
// straight away — the Stripe/Vercel pattern, so nothing is more than a few
// keystrokes away.
const CommandMenu = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { logoutUser } = useOnLogout();

  useEffect(() => {
    let gPressedAt = 0;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing =
        !!target && (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable);
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (typing || e.metaKey || e.ctrlKey || e.altKey) {
        return;
      } else if (e.key === '/') {
        e.preventDefault();
        setOpen(true);
      } else if (Date.now() - gPressedAt < 1200 && SHORTCUTS[e.key]) {
        e.preventDefault();
        gPressedAt = 0;
        navigate(`/${SHORTCUTS[e.key]}`);
      } else if (e.key === 'g') {
        gPressedAt = Date.now();
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_EVENT, onOpen);
    };
  }, [navigate]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const close = () => setOpen(false);

  const commands = useMemo<Command[]>(() => {
    const q = query.trim().toLowerCase();
    const go = (to: string) => () => {
      navigate(to);
      close();
    };

    const pages: Command[] = navGroups
      .flatMap((group) => group.items.map((item) => ({ item, group: group.label })))
      .filter(({ item, group }) =>
        !q ? true : `${item.title} ${item.keywords ?? ''} ${group ?? ''}`.toLowerCase().includes(q)
      )
      .map(({ item, group }) => ({
        id: `page-${item.path}`,
        group: 'Go to',
        label: item.title,
        hint: PAGE_GUIDES[item.path]?.summary ?? group,
        shortcut: item.shortcut ? `G ${item.shortcut.toUpperCase()}` : undefined,
        icon: item.icon,
        run: go(`/${item.path}`),
      }));

    const customer: Command[] = looksLikePhone(query)
      ? [
          {
            id: 'customer',
            group: 'Customer',
            label: (
              <>
                Open customer <strong>{formatPhone(toChatId(query))}</strong>
              </>
            ),
            hint: 'Everything about them on one page',
            icon: <UserOutlined />,
            run: go(`/${CUSTOMERS}/${toChatId(query)}`),
          },
        ]
      : [];

    const searches: Command[] = q
      ? SEARCHABLE.map((s) => ({
          id: `search-${s.path}`,
          group: 'Search',
          label: (
            <>
              Search {s.label} for <strong>“{query.trim()}”</strong>
            </>
          ),
          hint: s.hint,
          icon: <SearchOutlined />,
          run: go(`/${s.path}?search=${encodeURIComponent(query.trim())}`),
        }))
      : [];

    const help: Command[] =
      !q || 'help tips guide how welcome tour'.includes(q)
        ? [
            {
              id: 'tips',
              group: 'Help',
              label: 'Show page tips again',
              hint: 'Brings back “How this page works” everywhere',
              icon: <BulbOutlined />,
              run: () => {
                close();
                resetAllGuides();
              },
            },
          ]
        : [];

    const actions: Command[] =
      !q || 'sign out log out logout'.includes(q)
        ? [
            {
              id: 'logout',
              group: 'Account',
              label: 'Sign out',
              icon: <LogoutOutlined />,
              run: () => {
                close();
                logoutUser();
              },
            },
          ]
        : [];

    // Pages first when they match, so typing a page name and Enter just works.
    return pages.length
      ? [...customer, ...pages, ...searches, ...help, ...actions]
      : [...customer, ...searches, ...help, ...actions];
    // eslint-disable-next-line
  }, [query]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) return null;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, commands.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commands[active]?.run();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };

  let lastGroup = '';

  return (
    <Backdrop onMouseDown={close}>
      <Panel
        role='dialog'
        aria-modal='true'
        aria-label='Command menu'
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <InputRow>
          <SearchOutlined />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='Search or jump to…'
            aria-label='Search or jump to'
            role='combobox'
            aria-expanded='true'
            aria-controls='command-menu-list'
            aria-activedescendant={commands[active]?.id}
          />
          <Kbd>esc</Kbd>
        </InputRow>

        <List ref={listRef} id='command-menu-list' role='listbox'>
          {commands.length === 0 && <Empty>No results for “{query}”</Empty>}
          {commands.map((cmd, index) => {
            const heading = cmd.group !== lastGroup ? cmd.group : null;
            lastGroup = cmd.group;
            return (
              <React.Fragment key={cmd.id}>
                {heading && <GroupLabel>{heading}</GroupLabel>}
                <Item
                  id={cmd.id}
                  role='option'
                  aria-selected={index === active}
                  data-index={index}
                  className={index === active ? 'active' : ''}
                  onMouseMove={() => setActive(index)}
                  onClick={cmd.run}
                >
                  <span className='icon'>{cmd.icon}</span>
                  <span className='label'>{cmd.label}</span>
                  {cmd.hint && <span className='hint'>{cmd.hint}</span>}
                  {cmd.shortcut && <Kbd className='shortcut'>{cmd.shortcut}</Kbd>}
                </Item>
              </React.Fragment>
            );
          })}
        </List>

        <Footer>
          <span>
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd> to move
          </span>
          <span>
            <Kbd>↵</Kbd> to open
          </span>
          <span>
            <Kbd>G</Kbd> then a letter to jump
          </span>
          <span className='right'>
            <Kbd>{commandKeyLabel}</Kbd> anywhere
          </span>
        </Footer>
      </Panel>
    </Backdrop>
  );
};

export default CommandMenu;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 30000;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 12vh 16px 16px;
  background: rgba(14, 26, 19, 0.28);
  backdrop-filter: blur(2px);
  animation: cmdFade 0.12s ease-out;

  @keyframes cmdFade {
    from {
      opacity: 0;
    }
  }
`;

const Panel = styled.div`
  width: 100%;
  max-width: 600px;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  animation: cmdPop 0.14s ease-out;

  @keyframes cmdPop {
    from {
      opacity: 0;
      transform: scale(0.98) translateY(-4px);
    }
  }
`;

const InputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
  height: 56px;
  border-bottom: 1px solid var(--line);
  color: var(--muted);
  font-size: 16px;

  & input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: none;
    background: transparent;
    font-size: 16px;
    color: var(--ink);
    box-shadow: none !important;
  }

  & input::placeholder {
    color: #a9a395;
  }
`;

const List = styled.div`
  overflow-y: auto;
  padding: 6px;
`;

const GroupLabel = styled.div`
  padding: 10px 10px 6px;
  font-size: 12px;
  font-weight: 500;
  color: #7a8279;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 42px;
  padding: 0 10px;
  border-radius: 9px;
  font-size: 14px;
  color: var(--ink-2);
  cursor: pointer;

  & .icon {
    display: inline-flex;
    width: 18px;
    justify-content: center;
    color: var(--muted);
    font-size: 15px;
  }
  & .label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  & .label {
    flex: none;
    max-width: 55%;
  }
  & .hint {
    flex: 1;
    min-width: 0;
    text-align: right;
    font-size: 12px;
    color: #7a8279;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &.active {
    background: var(--ground);
    color: var(--ink);
  }

  @media (max-width: 560px) {
    & .hint {
      display: none;
    }
  }
`;

const Empty = styled.div`
  padding: 28px 12px;
  text-align: center;
  font-size: 14px;
  color: var(--muted);
`;

const Footer = styled.div`
  display: flex;
  gap: 16px;
  padding: 10px 16px;
  border-top: 1px solid var(--line);
  background: var(--panel);
  font-size: 12px;
  color: #7a8279;

  & > span {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
  & .right {
    margin-left: auto;
  }

  @media (max-width: 560px) {
    display: none;
  }
`;

export const Kbd = styled.kbd`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 5px;
  border: 1px solid var(--line);
  background: #ffffff;
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 500;
  color: var(--muted);
  box-shadow: 0 1px 0 var(--line);
`;
